import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import {
  X, MapPin, User, Phone, Package, Clock,
  Truck, CheckCircle, AlertTriangle, ChevronDown,
  Bike, Car, MessageCircle, UserCheck, DollarSign,
  ImageIcon, Pencil, Check, Plus, Upload, XCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL;

const MAX_IMAGES = 6;
const MAX_WIDTH_PX = 1400;
const JPEG_QUALITY = 0.75;

const STATUS_OPTIONS = [
  { value: 'pending', label: 'Pendiente', color: 'text-yellow-600' },
  { value: 'en_recojo', label: 'En recojo', color: 'text-orange-600' },
  { value: 'encamino', label: 'En camino', color: 'text-blue-600' },
  { value: 'entregado', label: 'Entregado', color: 'text-green-600' },
  { value: 'rechazado', label: 'Rechazado', color: 'text-red-600' },
  { value: 'cancelado', label: 'Cancelado', color: 'text-gray-500' },
  { value: 'postergado', label: 'Postergado', color: 'text-purple-600' },
];

const STATUS_COLORS = {
  pending: 'bg-yellow-100 text-yellow-800',
  en_recojo: 'bg-orange-100 text-orange-800',
  encamino: 'bg-blue-100 text-blue-800',
  entregado: 'bg-green-100 text-green-800',
  rechazado: 'bg-red-100 text-red-800',
  cancelado: 'bg-gray-100 text-gray-600',
  postergado: 'bg-purple-100 text-purple-800',
};

const STATUS_ICONS = {
  pending: Clock,
  en_recojo: Truck,
  encamino: Truck,
  entregado: CheckCircle,
  rechazado: AlertTriangle,
  cancelado: X,
  postergado: Clock,
};

function formatDateTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString('es-PE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function normalizeImages(images) {
  return (images || []).map((img, index) => ({
    ...img,
    _key: img.id || `${img.image_url}-${index}`,
  }));
}

function compressImage(file) {
  return new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(url);

      let { width, height } = img;
      if (width > MAX_WIDTH_PX) {
        height = Math.round((height * MAX_WIDTH_PX) / width);
        width = MAX_WIDTH_PX;
      }

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, width, height);

      canvas.toBlob(
        (blob) => {
          if (!blob) {
            resolve(file);
            return;
          }
          resolve(
            new File(
              [blob],
              file.name.replace(/\.[^.]+$/, '.jpg'),
              { type: 'image/jpeg' }
            )
          );
        },
        'image/jpeg',
        JPEG_QUALITY
      );
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(file);
    };

    img.src = url;
  });
}

function InlineEditField({ label, value, onSave, prefix = 'S/', color = 'text-gray-800' }) {
  const [editing, setEditing] = useState(false);
  const [input, setInput] = useState('');
  const [saving, setSaving] = useState(false);

  function startEdit() {
    setInput(value !== null && value !== undefined ? String(value) : '');
    setEditing(true);
  }

  async function confirmEdit() {
    setSaving(true);
    await onSave(input === '' ? null : parseFloat(input));
    setSaving(false);
    setEditing(false);
  }

  function cancelEdit() {
    setEditing(false);
  }

  return (
    <div className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
      <span className="text-xs text-gray-500">{label}</span>
      {editing ? (
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-gray-400">{prefix}</span>
          <input
            type="number"
            min="0"
            step="0.01"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            className="w-20 text-right text-sm border border-brand-primary/40 rounded-lg px-2 py-1 focus:outline-none focus:ring-1 focus:ring-brand-primary/30"
            autoFocus
            onKeyDown={(e) => {
              if (e.key === 'Enter') confirmEdit();
              if (e.key === 'Escape') cancelEdit();
            }}
          />
          <button
            onClick={confirmEdit}
            disabled={saving}
            className="p-1 bg-brand-primary text-white rounded-lg disabled:opacity-50"
          >
            <Check size={12} />
          </button>
          <button
            onClick={cancelEdit}
            className="p-1 bg-gray-100 text-gray-500 rounded-lg"
          >
            <X size={12} />
          </button>
        </div>
      ) : (
        <button onClick={startEdit} className="flex items-center gap-1.5 group">
          <span className={`text-sm font-semibold ${color}`}>
            {value !== null && value !== undefined ? `${prefix} ${Number(value).toFixed(2)}` : '—'}
          </span>
          <Pencil size={11} className="text-gray-300 group-hover:text-brand-primary transition-colors" />
        </button>
      )}
    </div>
  );
}

function ImageCarouselBlock({ title, images = [] }) {
  const normalized = normalizeImages(images);
  const [selected, setSelected] = useState(0);

  useEffect(() => {
    if (selected >= normalized.length) {
      setSelected(0);
    }
  }, [normalized.length, selected]);

  if (!normalized.length) return null;

  const current = normalized[selected];

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">
        <ImageIcon size={13} />
        {title} ({normalized.length})
      </div>

      <a
        href={current.image_url}
        target="_blank"
        rel="noreferrer"
        className="block rounded-2xl overflow-hidden border border-gray-200 bg-gray-50"
      >
        <img
          src={current.image_url}
          alt={title}
          className="w-full h-[260px] object-cover"
        />
      </a>

      <div className="flex gap-2 overflow-x-auto pb-1">
        {normalized.map((img, idx) => (
          <button
            key={img._key}
            onClick={() => setSelected(idx)}
            className={`shrink-0 rounded-xl overflow-hidden border-2 transition-all ${
              selected === idx ? 'border-brand-primary' : 'border-transparent'
            }`}
          >
            <img
              src={img.image_url}
              alt={`${title} ${idx + 1}`}
              className="w-20 h-20 object-cover"
            />
          </button>
        ))}
      </div>
    </div>
  );
}

function AdminImageUploadSection({ deliveryId, changedBy, onUploaded }) {
  const [imageType, setImageType] = useState('delivery');
  const [images, setImages] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [compressing, setCompressing] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  async function processFiles(rawFiles) {
    const onlyImages = Array.from(rawFiles || []).filter((f) => f.type?.startsWith('image/'));
    if (!onlyImages.length) return;

    const slots = MAX_IMAGES - images.length;
    const picked = onlyImages.slice(0, slots);
    if (!picked.length) return;

    setCompressing(true);
    setError('');

    try {
      const compressed = await Promise.all(picked.map(compressImage));
      setImages((prev) => [
        ...prev,
        ...compressed.map((file) => ({
          file,
          preview: URL.createObjectURL(file),
        })),
      ]);
    } finally {
      setCompressing(false);
    }
  }

  async function handleFileChange(e) {
    await processFiles(e.target.files);
    e.target.value = '';
  }

  async function handlePaste(e) {
    const files = Array.from(e.clipboardData?.items || [])
      .filter((item) => item.type?.startsWith('image/'))
      .map((item) => item.getAsFile())
      .filter(Boolean);

    if (files.length) {
      e.preventDefault();
      await processFiles(files);
    }
  }

  async function handleDrop(e) {
    e.preventDefault();
    setDragOver(false);
    await processFiles(e.dataTransfer?.files);
  }

  function removeImage(index) {
    setImages((prev) => {
      URL.revokeObjectURL(prev[index].preview);
      return prev.filter((_, i) => i !== index);
    });
  }

  async function uploadImages() {
    if (changedBy === undefined || changedBy === null || changedBy === '') {
      setError('No se pudo identificar el usuario que realiza el cambio');
      return;
    }

    if (!images.length) {
      setError('Agrega al menos una imagen');
      return;
    }

    setUploading(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('changed_by', String(changedBy));
      formData.append('user_id', String(changedBy));
      formData.append('image_type', imageType);
      images.forEach((img) => formData.append('images', img.file));

      const { data } = await axios.post(
        `${API_URL}/api/express/deliveries/${deliveryId}/images`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      images.forEach((img) => URL.revokeObjectURL(img.preview));
      setImages([]);

      if (onUploaded) onUploaded(data);
    } catch (err) {
      setError(err.response?.data?.error || 'Error subiendo imágenes');
    } finally {
      setUploading(false);
    }
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="px-4 py-3 bg-gray-50 border-b border-gray-100 flex items-center gap-2">
        <Upload size={14} className="text-brand-primary" />
        <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
          Agregar imágenes
        </span>
      </div>

      <div className="p-4 space-y-3">
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setImageType('delivery')}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium border ${
              imageType === 'delivery'
                ? 'bg-brand-primary text-white border-brand-primary'
                : 'bg-white text-gray-600 border-gray-200'
            }`}
          >
            Fotos de entrega
          </button>
          <button
            onClick={() => setImageType('pickup')}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium border ${
              imageType === 'pickup'
                ? 'bg-orange-500 text-white border-orange-500'
                : 'bg-white text-gray-600 border-gray-200'
            }`}
          >
            Fotos de recojo
          </button>
        </div>

        <div
          onPaste={handlePaste}
          onDrop={handleDrop}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          className={`rounded-2xl border-2 border-dashed p-4 transition-colors ${
            dragOver ? 'border-brand-primary bg-brand-primary/5' : 'border-gray-300 bg-gray-50'
          }`}
          tabIndex={0}
        >
          <div className="text-center space-y-2">
            <div className="mx-auto w-10 h-10 rounded-full bg-white border border-gray-200 flex items-center justify-center">
              <ImageIcon size={18} className="text-gray-400" />
            </div>
            <div className="text-sm font-medium text-gray-700">
              Arrastra imágenes aquí, pégalas o selecciónalas
            </div>
            <div className="text-xs text-gray-400">
              Soporta copiar/pegar, drag & drop y selección desde multimedia
            </div>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="inline-flex items-center gap-2 bg-white border border-gray-200 hover:bg-gray-100 rounded-xl px-4 py-2 text-sm font-medium text-gray-700"
            >
              <Plus size={14} />
              Seleccionar imágenes
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={handleFileChange}
              className="hidden"
            />
          </div>
        </div>

        {!!images.length && (
          <div className="flex gap-2 overflow-x-auto pb-1">
            {images.map((img, idx) => (
              <div key={`${img.preview}-${idx}`} className="relative shrink-0">
                <img
                  src={img.preview}
                  alt={`preview ${idx + 1}`}
                  className="w-20 h-20 object-cover rounded-xl border border-gray-200"
                />
                <button
                  onClick={() => removeImage(idx)}
                  className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full p-0.5 shadow"
                >
                  <XCircle size={15} />
                </button>
              </div>
            ))}
          </div>
        )}

        {(compressing || uploading) && (
          <div className="text-xs text-gray-400 animate-pulse">
            {compressing ? 'Procesando imágenes...' : 'Subiendo imágenes...'}
          </div>
        )}

        {error && (
          <div className="bg-red-50 text-red-600 text-xs rounded-xl px-3 py-2.5 flex items-center gap-2">
            <AlertTriangle size={13} className="shrink-0" />
            {error}
          </div>
        )}

        <div className="flex justify-end">
          <button
            onClick={uploadImages}
            disabled={!images.length || compressing || uploading}
            className="inline-flex items-center gap-2 bg-brand-primary text-white px-4 py-2 rounded-xl text-sm font-medium disabled:opacity-50"
          >
            <Upload size={14} />
            Subir imágenes
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ExpressDetailModal({ deliveryId, drivers, user_id, onClose, onUpdate }) {
  const [delivery, setDelivery] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showStatusMenu, setStatusMenu] = useState(false);
  const [pendingStatus, setPending] = useState(null);
  const [statusReason, setReason] = useState('');
  const [statusLoading, setStatusLoad] = useState(false);
  const [showDriverPicker, setDriverPicker] = useState(false);
  const [assignLoading, setAssignLoad] = useState(false);
  const [savingPayment, setSavingPayment] = useState(false);

  async function fetchDetail() {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/express/deliveries/${deliveryId}`);
      setDelivery(data);
    } catch (err) {
      console.error('Error fetching express detail:', err);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchDetail();
  }, [deliveryId]);

  async function confirmStatusChange() {
    if (!pendingStatus) return;
    setStatusLoad(true);
    try {
      await axios.patch(`${API_URL}/api/express/deliveries/${deliveryId}/status`, {
        status: pendingStatus,
        reason: statusReason,
        changed_by: user_id,
      });
      const updated = { ...delivery, status: pendingStatus };
      setDelivery(updated);
      onUpdate(updated);
      setPending(null);
      setReason('');
    } catch (err) {
      console.error('Error updating status:', err);
    } finally {
      setStatusLoad(false);
    }
  }

  async function assignDriver(driverUserId) {
    setAssignLoad(true);
    try {
      const { data } = await axios.patch(
        `${API_URL}/api/express/deliveries/${deliveryId}/assign-driver`,
        { driver_user_id: driverUserId, changed_by: user_id }
      );
      const updated = { ...delivery, driver: data.driver, driver_id: driverUserId };
      setDelivery(updated);
      onUpdate(updated);
      setDriverPicker(false);
    } catch (err) {
      console.error('Error assigning driver:', err);
    } finally {
      setAssignLoad(false);
    }
  }

  async function unassignDriver() {
    setAssignLoad(true);
    try {
      await axios.patch(`${API_URL}/api/express/deliveries/${deliveryId}/assign-driver`, {
        driver_user_id: null,
        changed_by: user_id,
      });
      const updated = { ...delivery, driver: null, driver_id: null };
      setDelivery(updated);
      onUpdate(updated);
    } finally {
      setAssignLoad(false);
    }
  }

  async function savePaymentField(field, value) {
    setSavingPayment(true);
    try {
      const { data } = await axios.patch(
        `${API_URL}/api/express/deliveries/${deliveryId}/payment`,
        { [field]: value }
      );
      const updated = { ...delivery, ...data };
      setDelivery(updated);
      onUpdate(updated);
    } catch (err) {
      console.error('Error saving payment field:', err);
    } finally {
      setSavingPayment(false);
    }
  }

  function openWhatsApp() {
    if (!delivery?.driver?.phone) return;
    const driverEarnings = (delivery.total_price * 0.75).toFixed(2);
    const text = encodeURIComponent(
      `🚀 *OPORTUNIDAD DE ENTREGA - FAST MAX*\n\n` +
      `📦 *Recojo:* ${delivery.pickup_address}\n` +
      `📍 *Entrega:* ${delivery.dropoff_address}\n` +
      `👤 *Destinatario:* ${delivery.recipient_name}\n` +
      `📏 *Distancia:* ${delivery.distance_km} km\n` +
      `🚗 *Vehículo:* ${delivery.vehicle_type === 'bike' ? 'Moto/Bicicleta' : 'Auto'}\n\n` +
      `💰 *Tu ganancia: S/ ${driverEarnings}*\n\n` +
      `¿Puedes tomar esta entrega?`
    );
    window.open(`https://wa.me/${delivery.driver.phone.replace(/\D/g, '')}?text=${text}`, '_blank');
  }

  function getPaymentMismatch() {
    if (!delivery?.charge) return null;
    const total =
      (delivery.charged || 0) +
      (delivery.fastmax_transfer_amount || 0) +
      (delivery.provider_transfer_amount || 0) +
      (delivery.fastmax_exchange_amount || 0);

    const diff = Math.abs(total - delivery.charge);
    if (diff < 0.01) return null;
    return { total, diff, over: total > delivery.charge };
  }

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
        <div className="bg-white rounded-2xl p-8 text-gray-400 text-sm">Cargando...</div>
      </div>
    );
  }

  if (!delivery) return null;

  const driverLabel = STATUS_OPTIONS.find((s) => s.value === delivery.status)?.label || delivery.status;
  const mismatch = getPaymentMismatch();
  const StatusIcon = STATUS_ICONS[delivery.status] || Clock;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="bg-white w-full sm:max-w-3xl sm:rounded-2xl rounded-t-2xl max-h-[92vh] flex flex-col font-montserrat">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${STATUS_COLORS[delivery.status] || 'bg-gray-100 text-gray-600'}`}>
              {driverLabel}
            </span>
            <span className="text-xs text-gray-400 font-mono">#{delivery.id}</span>
            {delivery.vehicle_type === 'bike' ? (
              <Bike size={15} className="text-gray-400" />
            ) : (
              <Car size={15} className="text-gray-400" />
            )}
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors">
            <X size={18} className="text-gray-500" />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 px-5 py-4 space-y-5">
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-gray-50 rounded-xl p-3 text-center">
              <div className="text-xs text-gray-400 mb-1">Total cobrado</div>
              <div className="font-bold text-gray-800">S/ {Number(delivery.total_price).toFixed(2)}</div>
            </div>
            <div className="bg-brand-primary/5 rounded-xl p-3 text-center">
              <div className="text-xs text-gray-400 mb-1">Fast Max (25%)</div>
              <div className="font-bold text-brand-primary">S/ {Number(delivery.fastmax_earnings).toFixed(2)}</div>
            </div>
            <div className="bg-brand-green/5 rounded-xl p-3 text-center">
              <div className="text-xs text-gray-400 mb-1">Conductor (75%)</div>
              <div className="font-bold text-brand-green">S/ {Number(delivery.driver_earnings).toFixed(2)}</div>
            </div>
          </div>

          <div className="bg-gray-50 rounded-xl px-4 py-3">
            <div className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
              <User size={15} />
              Usuario remitente
            </div>
            <div className="grid sm:grid-cols-2 gap-3 text-sm">
              <div>
                <div className="text-xs text-gray-400">Username</div>
                <div className="font-medium text-gray-800">{delivery.user_username || '—'}</div>
              </div>
              <div>
                <div className="text-xs text-gray-400">User ID</div>
                <div className="font-mono text-gray-700">{delivery.user_id ?? '—'}</div>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-start gap-3">
              <div className="mt-0.5 w-6 h-6 rounded-full bg-brand-green/15 flex items-center justify-center shrink-0">
                <MapPin size={12} className="text-brand-green" />
              </div>
              <div>
                <div className="text-xs text-gray-400">Recojo</div>
                <div className="text-sm text-gray-800">{delivery.pickup_address}</div>
              </div>
            </div>
            <div className="ml-3 w-px h-4 bg-gray-200" />
            <div className="flex items-start gap-3">
              <div className="mt-0.5 w-6 h-6 rounded-full bg-brand-primary/15 flex items-center justify-center shrink-0">
                <MapPin size={12} className="text-brand-primary" />
              </div>
              <div>
                <div className="text-xs text-gray-400">Entrega</div>
                <div className="text-sm text-gray-800">{delivery.dropoff_address}</div>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between bg-gray-50 rounded-xl px-4 py-3">
            <div className="flex items-center gap-3">
              <User size={16} className="text-gray-400" />
              <div>
                <div className="text-xs text-gray-400">Destinatario</div>
                <div className="text-sm font-medium text-gray-800">{delivery.recipient_name}</div>
              </div>
            </div>
            <a href={`tel:${delivery.recipient_phone}`} className="flex items-center gap-1.5 text-xs text-brand-primary font-medium">
              <Phone size={13} />
              {delivery.recipient_phone}
            </a>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-4 py-3 bg-gray-50 border-b border-gray-100 flex items-center gap-2">
              <DollarSign size={14} className="text-brand-gold" />
              <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Cobro</span>
              {savingPayment && <span className="ml-auto text-xs text-gray-400 animate-pulse">Guardando...</span>}
            </div>

            {mismatch && (
              <div className="px-4 py-2 bg-amber-50 border-b border-amber-100 flex items-center gap-2">
                <AlertTriangle size={13} className="text-amber-500 shrink-0" />
                <span className="text-xs text-amber-700">
                  El conductor registró S/ {mismatch.total.toFixed(2)} pero el cargo es S/ {Number(delivery.charge).toFixed(2)}
                  {' '}({mismatch.over ? `exceso S/ ${mismatch.diff.toFixed(2)}` : `falta S/ ${mismatch.diff.toFixed(2)}`})
                </span>
              </div>
            )}

            <div className="px-4 py-2">
              <InlineEditField
                label="Cargo al destinatario"
                value={delivery.charge}
                onSave={(v) => savePaymentField('charge', v)}
                color="text-brand-gold"
              />
              <InlineEditField
                label="Efectivo recibido"
                value={delivery.charged}
                onSave={(v) => savePaymentField('charged', v)}
                color="text-brand-green"
              />
              <InlineEditField
                label="Transferencia Fast Max"
                value={delivery.fastmax_transfer_amount}
                onSave={(v) => savePaymentField('fastmax_transfer_amount', v)}
                color="text-brand-primary"
              />
              <InlineEditField
                label="Transferencia proveedor"
                value={delivery.provider_transfer_amount}
                onSave={(v) => savePaymentField('provider_transfer_amount', v)}
                color="text-brand-gold"
              />
              <InlineEditField
                label="Vuelto"
                value={delivery.fastmax_exchange_amount}
                onSave={(v) => savePaymentField('fastmax_exchange_amount', v)}
                color="text-gray-500"
              />
            </div>
          </div>

          {delivery.pickup_driver_notes && (
            <div className="bg-orange-50 rounded-xl px-4 py-3 space-y-2">
              <div className="text-xs font-semibold text-orange-600 flex items-center gap-1.5">
                <Package size={13} />
                Nota de recojo
              </div>
              <div className="text-sm text-gray-700">{delivery.pickup_driver_notes}</div>
            </div>
          )}

          <ImageCarouselBlock title="Fotos de recojo" images={delivery.pickup_images} />
          <ImageCarouselBlock title="Fotos de entrega" images={delivery.delivery_images} />

          <AdminImageUploadSection
            deliveryId={deliveryId}
            changedBy={user_id}
            onUploaded={() => fetchDetail()}
          />

          <div className="bg-gray-50 rounded-xl px-4 py-3 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                <UserCheck size={15} />
                Conductor
              </div>
              <div className="flex gap-2">
                {delivery.driver && (
                  <button
                    onClick={openWhatsApp}
                    className="flex items-center gap-1.5 bg-green-500 hover:bg-green-600 text-white text-xs px-3 py-1.5 rounded-lg font-medium transition-colors"
                  >
                    <MessageCircle size={13} />
                    WhatsApp
                  </button>
                )}
                <button
                  onClick={() => setDriverPicker((v) => !v)}
                  disabled={assignLoading}
                  className="flex items-center gap-1.5 bg-brand-primary hover:bg-brand-primary/90 text-white text-xs px-3 py-1.5 rounded-lg font-medium transition-colors disabled:opacity-60"
                >
                  {delivery.driver ? 'Reasignar' : 'Asignar conductor'}
                </button>
              </div>
            </div>

            {delivery.driver ? (
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium text-gray-800">{delivery.driver.name}</div>
                  <div className="text-xs text-gray-400">{delivery.driver.phone}</div>
                </div>
                <button
                  onClick={unassignDriver}
                  disabled={assignLoading}
                  className="text-xs text-red-400 hover:text-red-600 disabled:opacity-60"
                >
                  Desasignar
                </button>
              </div>
            ) : (
              <div className="text-xs text-red-400 font-medium">Sin conductor asignado</div>
            )}

            {showDriverPicker && (
              <div className="mt-1 border border-gray-200 rounded-xl bg-white overflow-hidden max-h-48 overflow-y-auto">
                {drivers.length === 0 ? (
                  <div className="px-4 py-3 text-xs text-gray-400">No hay conductores disponibles</div>
                ) : (
                  drivers.map((drv) => (
                    <button
                      key={drv.user_id}
                      onClick={() => assignDriver(drv.user_id)}
                      disabled={assignLoading || drv.user_id === delivery.driver_id}
                      className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-gray-50 transition-colors disabled:opacity-40 text-left"
                    >
                      <div>
                        <div className="text-sm font-medium text-gray-800">{drv.username}</div>
                        <div className="text-xs text-gray-400">{drv.phone_number}</div>
                      </div>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        drv.active_express_count > 0
                          ? 'bg-yellow-100 text-yellow-700'
                          : 'bg-green-100 text-green-700'
                      }`}>
                        {drv.active_express_count > 0 ? `${drv.active_express_count} activa(s)` : 'Libre'}
                      </span>
                    </button>
                  ))
                )}
              </div>
            )}
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold text-gray-700">Cambiar estado</span>
              <div className="relative">
                <button
                  onClick={() => setStatusMenu((v) => !v)}
                  className="flex items-center gap-2 border border-gray-200 rounded-lg px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
                >
                  {pendingStatus
                    ? STATUS_OPTIONS.find((s) => s.value === pendingStatus)?.label
                    : 'Seleccionar'}
                  <ChevronDown size={13} />
                </button>
                {showStatusMenu && (
                  <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-10 w-44 overflow-hidden">
                    {STATUS_OPTIONS.map((opt) => (
                      <button
                        key={opt.value}
                        onClick={() => {
                          setPending(opt.value);
                          setStatusMenu(false);
                        }}
                        className={`w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 ${opt.color}`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {pendingStatus && (
              <div className="space-y-2 bg-gray-50 rounded-xl p-3">
                <textarea
                  rows={2}
                  placeholder="Motivo (opcional)"
                  value={statusReason}
                  onChange={(e) => setReason(e.target.value)}
                  className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-primary/30 resize-none"
                />
                <div className="flex gap-2 justify-end">
                  <button
                    onClick={() => setPending(null)}
                    className="text-xs text-gray-500 px-3 py-1.5 rounded-lg border border-gray-200"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={confirmStatusChange}
                    disabled={statusLoading}
                    className="text-xs bg-brand-primary text-white px-4 py-1.5 rounded-lg font-medium disabled:opacity-60"
                  >
                    {statusLoading ? 'Guardando...' : 'Confirmar'}
                  </button>
                </div>
              </div>
            )}
          </div>

          {delivery.items && delivery.items.length > 0 && (
            <div>
              <div className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                <Package size={15} />
                Productos ({delivery.items.length})
              </div>
              <div className="divide-y divide-gray-100 border border-gray-100 rounded-xl overflow-hidden">
                {delivery.items.map((item) => (
                  <div key={item.id} className="flex items-center justify-between px-4 py-2.5 text-sm">
                    <div>
                      <div className="font-medium text-gray-800">{item.product_name}</div>
                      <div className="text-xs text-gray-400 font-mono">{item.product_sku}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs text-gray-500">x{item.quantity}</div>
                      {item.unit_price && (
                        <div className="text-xs text-gray-400">S/ {Number(item.unit_price).toFixed(2)}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {delivery.status_logs && delivery.status_logs.length > 0 && (
            <div>
              <div className="text-sm font-semibold text-gray-700 mb-3">Historial de estados</div>
              <div className="space-y-2">
                {delivery.status_logs.map((log, idx) => (
                  <div key={log.id} className="flex gap-3 items-start">
                    <div className={`mt-0.5 w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${
                      idx === delivery.status_logs.length - 1
                        ? 'bg-brand-primary/15 text-brand-primary'
                        : 'bg-gray-100 text-gray-400'
                    }`}>
                      <StatusIcon size={13} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium text-gray-800">
                          {log.to_status}
                        </span>
                        <span className="text-xs text-gray-400">
                          {formatDateTime(log.created_at)}
                        </span>
                      </div>
                      {log.reason && (
                        <div className="text-xs text-gray-500 mt-0.5">{log.reason}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}